// mediaKinds — what may be attached to a job, and how a row of it is drawn.
//
// ── The gap this closes (docs/PARITY_2026-09-08.md §6) ──────────────────────
//
// The classic app called the panel "Attachments" and took `accept="*/*"`: a
// PDF permit, a signed change order, a scanned receipt. The rebuild's two
// upload inputs both said `accept="image/*"`, so a permit had nowhere to live
// and a crew member standing in front of a document could do nothing with it.
// Worse, both screens rendered every row as an unconditional `<img>`, so a
// PDF uploaded before the rebuild showed as a broken image.
//
// The SERVER never lost the capability. `server/routes/media.js` accepts pdf,
// doc, docx, xls, xlsx, txt and csv (its `allowedMimes` and `allowedExts`),
// stores `file_type` as 'pdf' or 'document', and `office/insight/Media.js`
// has offered PDFs and Documents filters the whole time. Only the two inputs
// were narrow. So this module is a CLIENT declaration measured against that
// server file, not a new capability — mediaKinds.test.js reads
// server/routes/media.js and fails if this list ever asks for something the
// server would answer 400 to.
//
// ── Why the accept list is not just what the server allows ──────────────────
//
// `server/server.js` serves /uploads with a `sandbox` Content-Security-Policy
// for `.svg|.svgz|.htm|.html|.xhtml|.xml`, because an SVG opened DIRECTLY is a
// DOCUMENT and inline script in it runs in this app's origin, where the
// session token lives. The server's own filter takes any `image/*`, svg
// included; this list must never NAME one of those extensions and so widen an
// upload path towards that hole. mediaKinds.test.js asserts that too, against
// server.js's own SANDBOXED regex rather than a copy of it.
//
// `image/*` itself is carried over unchanged from the inputs this replaces —
// it is what makes a phone offer the camera roll, and dropping it would break
// the one thing that does work today. What is NEW here is only the document
// half, and there is nothing executable in it.

/** Extensions the picker names. Documents only — see the CSP note above. */
export const DOC_EXTS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv'];

/**
 * The `accept` attribute for an input that takes anything a job may carry.
 *
 * Mime types AND extensions, deliberately: an Android picker filters on the
 * mime type and ignores an extension it does not know, while iOS honours the
 * extension. Naming only one of the two hides half the files on half the
 * phones.
 */
export const MEDIA_ACCEPT = [
  'image/*',
  'application/pdf',
  'text/plain',
  'text/csv',
  ...DOC_EXTS.map((e) => `.${e}`),
].join(',');

/** The `accept` for the CAMERA control — photos only, unchanged. */
export const PHOTO_ACCEPT = 'image/*';

const IMAGE_NAME = /\.(jpe?g|png|gif|webp|heic|heif|bmp|avif)$/i;
const SHEET_NAME = /\.(xlsx?|csv)$/i;

const nameOf = (item) => String((item && (item.original_name || item.filename || item.name)) || '');
const typeOf = (item) => String((item && item.file_type) || '').toLowerCase();

/**
 * Is this row a picture — i.e. does a thumbnail mean anything for it?
 *
 * `file_type` is what the upload handler wrote: 'image', 'pdf' or 'document'.
 * It is believed when it is there. It is NOT always there: rows predating the
 * rebuild carry a raw mime type ('image/jpeg') and a few carry nothing at all,
 * and both of those rendered as a broken `<img>` on the job screen. So a mime
 * type counts as itself, and a row with no type at all falls back to its name.
 */
export function isImageFile(item) {
  const t = typeOf(item);
  if (t) return t === 'image' || t.startsWith('image/');
  // No type at all. The stored FILENAME is the last word — it is in the path
  // whether or not `original_name` came back, and the upload handler derives
  // the extension from the mime type when the phone sent none
  // (routes/media.js `mimeToExt`), so the path is a reliable second source.
  return IMAGE_NAME.test(nameOf(item) || fileHref(item));
}

/**
 * The Icon for a non-image row. Same three-way split the classic panel used
 * (JobMedia.js getFileIcon): a spreadsheet gets the sheet glyph, everything
 * else gets the document one. An invalid Icon name renders NOTHING, so both
 * of these are names that exist in ui/components/Icon.js.
 */
export function fileIcon(item) {
  if (isImageFile(item)) return 'image';
  if (SHEET_NAME.test(nameOf(item)) || typeOf(item) === 'spreadsheet') return 'reports';
  return 'document';
}

/** What to call the row when it has no caption: its own filename. */
export function fileLabel(item) {
  return nameOf(item) || (isImageFile(item) ? 'Photo' : 'File');
}

const DASH = '—';

/**
 * A size a person reads. Lifted out of office/insight/Media.js on 2026-09-09
 * so the job panels and the phone can print the same string; Media.js
 * re-exports it and its own call sites are unchanged.
 *
 * A missing size is a DASH, never "0 B": job_media.file_size is nullable and
 * a zero is a claim about the file.
 */
export function fmtBytes(bytes) {
  if (bytes === null || bytes === undefined || bytes === '') return DASH;
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return DASH;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/**
 * The URL of a row, whichever answer it came out of.
 *
 * GET /api/media/job/:id returns `file_path`; GET /api/field/job/:id aliases
 * the same column to `url`. One accessor, so a component can be handed either
 * without the caller remembering which endpoint it asked.
 */
export function fileHref(item) {
  return (item && (item.file_path || item.url)) || '';
}

/** The images of a list, in list order — what a viewer can page through. */
export function imagesOf(rows) {
  return (rows || []).filter(isImageFile);
}
